import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDT } from "@/lib/timefmt";
import { toast } from "sonner";
import { SortTh, FilterTh, filterRows, sortRows } from "@/components/tableTools";

function paramsSummary(p) {
  if (!p) return "—";
  if (p.mode) {
    let s = p.mode.replace(/_/g, " ");
    if (p.date) s += ` · ${p.date}`;
    if (p.from && p.to) s += ` · ${p.from} → ${p.to}`;
    if (p.include_exported) s += " · incl. exported";
    return s;
  }
  if (p.all) return "all data";
  if (p.from && p.to) return `${p.from} → ${p.to}`;
  return "all data";
}

export default function ExportLogTab() {
  const { user } = useAuth();
  const isSuper = (user?.role === "admin" && !user?.admin_level) || user?.admin_level === "global";
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [logs, setLogs] = useState([]);
  const [sort, setSort] = useState({ key: "when", dir: "desc" });
  const [qf, setQf] = useState({ when: "", by: "", type: "", scope: "", count: "" });

  const logText = (l) => ({
    when: fmtDT(l.created_at),
    by: `${l.user_name || ""} ${l.user_email || ""}`,
    type: l.kind || "",
    scope: paramsSummary(l.params),
    count: l.count,
  });
  const logKey = (l, k) => (k === "when" ? l.created_at || "" : k === "by" ? l.user_name || "" : k === "count" ? l.count ?? 0 : logText(l)[k] ?? "");
  const shown = sortRows(filterRows(logs, logText, qf), sort, logKey);

  useEffect(() => {
    api.get("/companies").then((r) => {
      setCompanies(r.data);
      setCompanyId((prev) => prev || (isSuper ? r.data[0]?.id : user?.company_id) || "");
    }).catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!companyId) return;
    api.get(`/admin/export-logs?company_id=${companyId}`).then((r) => setLogs(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-5">
      {isSuper && companies.length > 1 && (
        <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60 max-w-sm">
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Company</label>
          <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="exportlog-company-select"
            className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      )}
      <div className="rounded-lg border border-slate-800 bg-slate-900/60 overflow-hidden" data-testid="export-log-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800 text-left">
                <SortTh label="When" k="when" sort={sort} setSort={setSort} />
                <SortTh label="By" k="by" sort={sort} setSort={setSort} />
                <SortTh label="Type" k="type" sort={sort} setSort={setSort} />
                <SortTh label="Scope" k="scope" sort={sort} setSort={setSort} />
                <SortTh label="Entries" k="count" sort={sort} setSort={setSort} />
              </tr>
              <tr className="border-b border-slate-800 text-left">
                <FilterTh value={qf.when} onChange={(v) => setQf((f) => ({ ...f, when: v }))} testId="exportlog-qfilter-when" />
                <FilterTh value={qf.by} onChange={(v) => setQf((f) => ({ ...f, by: v }))} testId="exportlog-qfilter-by" />
                <FilterTh value={qf.type} onChange={(v) => setQf((f) => ({ ...f, type: v }))} testId="exportlog-qfilter-type" />
                <FilterTh value={qf.scope} onChange={(v) => setQf((f) => ({ ...f, scope: v }))} testId="exportlog-qfilter-scope" />
                <FilterTh value={qf.count} onChange={(v) => setQf((f) => ({ ...f, count: v }))} testId="exportlog-qfilter-count" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/70">
              {shown.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No exports recorded yet.</td></tr>
              )}
              {shown.map((l) => (
                <tr key={l.id} className="hover:bg-slate-900/60" data-testid={`exportlog-row-${l.id}`}>
                  <td className="px-4 py-3 font-mono-num text-xs text-slate-300">{fmtDT(l.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="text-sm">{l.user_name}</div>
                    <div className="text-xs text-slate-500">{l.user_email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-widest ${
                      l.kind === "admin" ? "bg-blue-600/20 text-blue-300 border border-blue-600/40" : "bg-slate-800 text-slate-300 border border-slate-700"}`}>
                      {l.kind}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-300 font-mono-num">{paramsSummary(l.params)}</td>
                  <td className="px-4 py-3 font-mono-num text-slate-200">{l.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
